// Detects a webserver restart from the browser and reloads the page to
// reconnect. The backend stamps every response with `X-Tiger-Instance`, a
// process-lifetime id. Every web page (main window and tool windows) remembers
// the first id it sees; when a later SUCCESSFUL response carries a different
// id, the backend has restarted and this page's one-shot mount state (views
// tree, themes, layouts, replay session) is stale — reload to rebuild it.
//
// Reloading re-runs the auth gate: auth sessions persist to disk across
// restarts, so a valid cookie reconnects seamlessly; a lost/invalid session
// lands on the login screen (the logout fallback). Because the check only
// fires on responses that actually arrived, nothing reloads while the backend
// is down — the existing 1 s polls just keep failing silently until it
// returns, and the first answered poll triggers the reload.
// Desktop (Tauri) never talks to the webserver and is unaffected.

const INSTANCE_HEADER = "X-Tiger-Instance";

let knownInstance: string | null = null;
let reloading = false;

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** Test-only: forget the remembered instance id. */
export function _resetBackendRestartWatch(): void {
  knownInstance = null;
  reloading = false;
}

function noteInstance(id: string | null, reload: () => void): void {
  if (!id || reloading) return;
  if (knownInstance === null) {
    knownInstance = id;
    return;
  }
  if (id !== knownInstance) {
    reloading = true;
    reload();
  }
}

/** Install a one-time `window.fetch` wrapper that watches the
 *  `X-Tiger-Instance` response header and reloads the page when it changes
 *  (webserver restarted). No-op on desktop or outside a browser. */
export function installBackendRestartWatch(
  reload: () => void = () => window.location.reload(),
): void {
  if (typeof window === "undefined" || isTauri()) return;
  const original = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const res = await original(input, init);
    noteInstance(res?.headers?.get?.(INSTANCE_HEADER) ?? null, reload);
    return res;
  };
}
